import { Dispatch, SetStateAction } from "react";

import { TableColumn } from "@/components/Table/Table";
import { SortParams } from "@/types/types";

import {
	renderContactSelectColumn,
	renderProspectNameColumn,
	renderProspectCompanyColumn,
	renderProspectSeniorityColumn,
	renderProspectLocationColumn,
	renderContactJobTitleColumn,
	renderContactPersonCountryColumn,
	renderContactCompanyCountryColumn,
	renderContactCompanyIndustryColumn,
	renderContactNormalizedCompanyNameColumn,
	renderContactCompanySizeColumn,
	renderContactCompanyFoundedYearColumn,
	renderContactGenderColumn,
	renderContactSalutationDeColumn,
	renderContactSalutationEnColumn,
	renderContactLeadScoringColumn,
	renderContactCompanyCategoryColumn,
	renderContactPersonaCategoryColumn,
	renderContactOriginListColumn,
	renderContactListsColumn,
	renderContactProfileTrackerColumn,
	renderContactLastAssignStatusColumn,
	renderContactLastSentimentColumn,
	renderContactFirstSentimentColumn,
	renderContactBestSentimentColumn,
	renderContactLastContactAtColumn,
	renderContactLastResponseAtColumn,
} from "../../utils/allColumns";
import {
	renderContactHeadlineColumn,
	renderContactAboutColumn,
	renderContactLanguageColumn,
	renderContactConnectionsColumn,
	renderContactFollowersColumn,
	renderContactReportedEmployeesColumn,
	renderContactRoleChangeColumn,
	renderContactPreviousEmployersColumn,
	renderContactPreviousEmployerColumn,
	renderContactPreviousJobTitleColumn,
} from "../../utils/contactProfileColumns";
import {
	renderContactPersonCityColumn,
	renderContactPersonStateColumn,
	renderContactCompanyCityColumn,
	renderContactCompanyStateColumn,
	renderContactCreatedAtColumn,
	renderContactPersonaScoreColumn,
	renderContactCompanyScoreColumn,
	renderContactResearchColumn,
} from "../../utils/contactLocationScoreColumns";

export interface ContactsTableColumnsProps {
	handleCopyClick: (value: string) => void;
	ts: (key: string) => string;
	sortingInbox: SortParams;
	setShowEdit: (val: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	setShowAll: Dispatch<SetStateAction<boolean>>;
	toggleSelectAll: () => void;
	selectAll: boolean;
	selectedItems: number[];
	handleSelectItem: (id: number) => void;
	onAddToTracker: (prospectId: number) => void;
}

/**
 * Data columns shown by default, in order (PREC-3567). Every other data column defaults to hidden.
 * Kept in sync with the List detail leads table (see `GeneralTableColumns`).
 */
const DEFAULT_VISIBLE_COLUMN_UIDS = new Set<string>([
	"prospectName",
	"prospectCompany",
	"contactJobTitle", // Job title
	"contactRoleChange",
	"contactConnections", // Connected on LinkedIn
	"contactPersonCountry", // Personal Country
	"contactCompanyCountry", // Company Country
	"contactCompanyCategory", // Company Category
	"contactCompanySize", // Number of employees
	"contactReportedEmployees", // Reported employees
	"contactPersonaCategory", // Persona Category
	"contactCreatedAt", // Added at
]);

/** Control columns whose default visibility is not governed by the PREC-3567 default set. */
const CONTEXTUAL_COLUMN_UIDS = new Set<string>(["contactSelect", "contactLeadScoring"]);

const applyDefaultVisibility = (columns: TableColumn[]): TableColumn[] =>
	columns.map((column) => {
		const uid = column.uid ?? "";

		if (CONTEXTUAL_COLUMN_UIDS.has(uid)) {
			return column;
		}

		return { ...column, show: DEFAULT_VISIBLE_COLUMN_UIDS.has(uid) };
	});

export const ContactsTableColumns = ({
	handleCopyClick,
	ts,
	sortingInbox,
	setShowEdit,
	showEdit,
	setShowAll,
	toggleSelectAll,
	selectAll,
	selectedItems,
	handleSelectItem,
	onAddToTracker,
}: ContactsTableColumnsProps): TableColumn[] => {
	// Default column order (PREC-3567): control columns, then the default-visible data columns in the
	// agreed sequence, then the remaining (hidden-by-default) columns. Visibility is applied below.
	const columns: TableColumn[] = [
		renderContactSelectColumn(
			setShowAll,
			toggleSelectAll,
			selectAll,
			selectedItems,
			handleSelectItem
		),
		renderContactLeadScoringColumn(ts, sortingInbox),
		// Default-visible data columns, in order.
		renderProspectNameColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit),
		renderProspectCompanyColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit),
		renderContactJobTitleColumn(ts, handleCopyClick, sortingInbox), // Job title
		renderContactRoleChangeColumn(ts),
		renderContactConnectionsColumn(ts, sortingInbox), // Connected on LinkedIn
		renderContactPersonCountryColumn(ts, handleCopyClick, sortingInbox), // Personal Country
		renderContactCompanyCountryColumn(ts, handleCopyClick, sortingInbox), // Company Country
		renderContactCompanyCategoryColumn(ts, sortingInbox), // Company Category
		renderContactCompanySizeColumn(ts, handleCopyClick, sortingInbox), // Number of employees
		renderContactReportedEmployeesColumn(ts, handleCopyClick, sortingInbox), // Reported employees
		renderContactPersonaCategoryColumn(ts, sortingInbox), // Persona Category
		renderContactCreatedAtColumn(ts, sortingInbox), // Added at
		// Remaining data columns — hidden by default.
		renderContactProfileTrackerColumn(ts, onAddToTracker),
		renderContactResearchColumn(ts),
		renderContactHeadlineColumn(ts, handleCopyClick, sortingInbox),
		renderContactAboutColumn(ts, handleCopyClick, sortingInbox),
		renderContactLanguageColumn(ts, sortingInbox),
		renderContactFollowersColumn(ts, sortingInbox),
		renderContactPreviousEmployersColumn(ts),
		renderContactPreviousEmployerColumn(ts),
		renderContactPreviousJobTitleColumn(ts),
		renderContactGenderColumn(ts, handleCopyClick, sortingInbox, setShowEdit, showEdit),
		renderContactSalutationDeColumn(ts),
		renderContactSalutationEnColumn(ts),
		renderContactPersonStateColumn(ts, handleCopyClick, sortingInbox),
		renderContactPersonCityColumn(ts, handleCopyClick, sortingInbox),
		renderContactCompanyStateColumn(ts, handleCopyClick, sortingInbox),
		renderContactCompanyCityColumn(ts, handleCopyClick, sortingInbox),
		renderContactNormalizedCompanyNameColumn(ts, handleCopyClick, sortingInbox),
		renderContactCompanyIndustryColumn(ts, handleCopyClick, sortingInbox),
		renderContactCompanyFoundedYearColumn(ts, handleCopyClick, sortingInbox),
		renderContactPersonaScoreColumn(ts, sortingInbox),
		renderContactCompanyScoreColumn(ts, sortingInbox),
		renderContactOriginListColumn(ts),
		renderContactListsColumn(ts),
		renderContactLastContactAtColumn(ts, sortingInbox),
		renderContactLastResponseAtColumn(ts, sortingInbox),
		renderContactLastAssignStatusColumn(ts),
		renderContactLastSentimentColumn(ts),
		renderContactFirstSentimentColumn(ts),
		renderContactBestSentimentColumn(ts),
		renderProspectSeniorityColumn(ts, sortingInbox),
		renderProspectLocationColumn(ts, handleCopyClick, sortingInbox),
	];

	return applyDefaultVisibility(columns);
};
